import './pdf.css';
import { useEffect, useRef, useState, type ReactNode } from 'react';
import type { PDFDocumentLoadingTask, PDFDocumentProxy, RenderTask } from 'pdfjs-dist/legacy/build/pdf.mjs';
import { t } from '../i18n';
import { loadPdfJs, PDF_ASSETS } from '../lib/pdfjs';

// PDF-Vorschau für Handys und die Android-App: Deren Browser zeigen PDFs nicht selbst an (Chrome am Handy nicht, die
// WebView der App auch nicht). Dort zeichnet pdf.js die Seiten untereinander; es wird erst geladen, wenn eine PDF
// aufgeht. Am Rechner bleibt die Anzeige des Browsers (Zoom, Suche, Drucken).

// Kann der Browser die PDF selbst im Fenster zeigen? Nicht auf Touch-Geräten (Handy, Tablet, App).
export function canShowPdf() {
  if (typeof navigator !== 'undefined' && navigator.pdfViewerEnabled === false) return false;
  return typeof matchMedia === 'undefined' || !matchMedia('(pointer: coarse)').matches;
}

// In der App gibt es kein Zoomen mit zwei Fingern, darum „+“ und „−“.
const ZOOM = [1, 1.5, 2, 3];
// höchstens so viele Bildpunkte je Seite in der Breite (Speicher am Handy)
const MAX_PX = 2000;

interface Size {
  w: number;
  h: number;
}

// Seiten der PDF untereinander, im Rahmen scrollbar. `fallback` erscheint, wenn die Datei nicht lädt oder keine PDF ist.
export function PdfPages({ url, title, fallback }: { url: string; title: string; fallback: ReactNode }) {
  const box = useRef<HTMLDivElement>(null);
  const [doc, setDoc] = useState<{ pdf: PDFDocumentProxy; sizes: Size[] } | null>(null);
  const [failed, setFailed] = useState(false);
  const [zoom, setZoom] = useState(0);
  const [width, setWidth] = useState(0);

  useEffect(() => {
    let gone = false;
    let task: PDFDocumentLoadingTask | null = null;
    setDoc(null);
    setFailed(false);
    (async () => {
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const data = new Uint8Array(await res.arrayBuffer());
      const lib = await loadPdfJs();
      if (gone) return;
      task = lib.getDocument({ data, ...PDF_ASSETS });
      const pdf = await task.promise;
      const sizes: Size[] = [];
      for (let i = 1; i <= pdf.numPages; i++) {
        const v = (await pdf.getPage(i)).getViewport({ scale: 1 });
        sizes.push({ w: v.width, h: v.height });
      }
      if (!gone) setDoc({ pdf, sizes });
    })().catch(() => {
      if (!gone) setFailed(true);
    });
    return () => {
      gone = true;
      void task?.destroy();
    };
  }, [url]);

  // Breite des Rahmens (ändert sich beim Drehen des Handys)
  useEffect(() => {
    const el = box.current;
    if (!el) return;
    const measure = () => setWidth(el.clientWidth - 16);
    measure();
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [failed]);

  if (failed) return <>{fallback}</>;
  const pageWidth = Math.max(0, Math.floor(width * ZOOM[zoom]!));
  const n = doc?.sizes.length ?? 0;

  return (
    <div className="pdfp" role="group" aria-label={t('PDF {title}', { title })}>
      <div className="pdfp-bar">
        <span>{doc ? (n === 1 ? t('1 Seite') : t('{n} Seiten', { n })) : t('PDF wird geladen …')}</span>
        <span className="pdfp-zoom">
          <button type="button" aria-label={t('Kleiner')} disabled={!doc || zoom === 0} onClick={() => setZoom((z) => z - 1)}>
            −
          </button>
          <span className="num" aria-live="polite">
            {Math.round(ZOOM[zoom]! * 100)} %
          </span>
          <button
            type="button"
            aria-label={t('Größer')}
            disabled={!doc || zoom === ZOOM.length - 1}
            onClick={() => setZoom((z) => z + 1)}
          >
            +
          </button>
        </span>
      </div>
      <div className="pdfp-pages" ref={box}>
        {doc &&
          pageWidth > 0 &&
          doc.sizes.map((s, i) => (
            <Page
              key={i}
              pdf={doc.pdf}
              n={i + 1}
              size={s}
              width={pageWidth}
              root={box}
              label={t('Seite {page} von {n}', { page: i + 1, n })}
            />
          ))}
      </div>
    </div>
  );
}

// Eine Seite: Platzhalter in der richtigen Größe, gezeichnet nur in der Nähe des sichtbaren Bereichs (sonst wieder frei,
// damit lange PDFs am Handy nicht zu viel Speicher brauchen).
function Page(p: {
  pdf: PDFDocumentProxy;
  n: number;
  size: Size;
  width: number;
  root: React.RefObject<HTMLDivElement | null>;
  label: string;
}) {
  const holder = useRef<HTMLDivElement>(null);
  const canvas = useRef<HTMLCanvasElement>(null);
  const [near, setNear] = useState(false);

  useEffect(() => {
    const el = holder.current;
    if (!el) return;
    const io = new IntersectionObserver((entries) => setNear(entries.some((e) => e.isIntersecting)), {
      root: p.root.current,
      rootMargin: '100% 0px',
    });
    io.observe(el);
    return () => io.disconnect();
  }, [p.root]);

  useEffect(() => {
    const c = canvas.current;
    if (!c) return;
    if (!near) {
      c.width = 0;
      c.height = 0;
      return;
    }
    let gone = false;
    let task: RenderTask | null = null;
    void p.pdf.getPage(p.n).then((page) => {
      if (gone) return;
      const px = Math.min(p.width * Math.min(window.devicePixelRatio || 1, 2), MAX_PX);
      const viewport = page.getViewport({ scale: px / p.size.w });
      c.width = Math.floor(viewport.width);
      c.height = Math.floor(viewport.height);
      task = page.render({ canvas: c, viewport });
      task.promise.catch(() => {});
    });
    return () => {
      gone = true;
      task?.cancel();
    };
  }, [near, p.pdf, p.n, p.width, p.size.w]);

  return (
    <div
      ref={holder}
      className="pdfp-page"
      style={{ width: p.width, height: Math.round((p.width * p.size.h) / p.size.w) }}
      role="img"
      aria-label={p.label}
    >
      <canvas ref={canvas} />
    </div>
  );
}
